<script lang="ts">
  import axios from 'axios'
  import PubSub from 'pubsub-js'
  import Swal from 'sweetalert2'
  import { onMount, type Component } from 'svelte'
  import { lang } from '@/store'
  import { i18n, showErrorMessage } from '@/plugins/util'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import PageHome from '@/components/appDriver/PageHome.svelte'
  import { currentPage } from '@/components/appDriver/store'
  import PageWalletPayNow from '@/components/appDriver/wallet/PageWalletPayNow.svelte'
  import PageWalletWithdraw from '@/components/appDriver/wallet/PageWalletWithdraw.svelte'
  import WalletSummaryCard from '@/components/appDriver/wallet/WalletSummaryCard.svelte'
  import { getDriverLocationPermissionState, requestDriverLocationPermission } from '@/components/appDriver/wallet/pageHelpers'
  import { getWalletDetail } from '@/components/appDriver/wallet/service'
  import { setCachedWalletSummary } from '@/components/appDriver/wallet/store'
  import type { WalletSummary } from '@/components/appDriver/wallet/types'

  const WALLET_LOCATION_PERMISSION_REQUESTED_KEY = 'LOCA_WALLET_LOCATION_PERMISSION_REQUESTED'

  let walletSummary: WalletSummary | null = null
  let isLoadingWalletDetail = false
  let isPreparingWalletAction = false
  let errorMessage = ''

  onMount(() => {
    $currentPage = 'wallet'
    void fetchWalletDetail()
  })

  function getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return error.response.data?.message ?? i18n($lang, ['Cannot load wallet detail', 'ບໍ່ສາມາດໂຫຼດຂໍ້ມູນກະເປົາເງິນໄດ້'])
      }

      if (error.request) {
        return i18n($lang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່'])
      }
    }

    return i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່'])
  }

  async function fetchWalletDetail() {
    isLoadingWalletDetail = true
    errorMessage = ''

    try {
      walletSummary = await getWalletDetail({
        lan: $lang,
      })
      setCachedWalletSummary(walletSummary)
    } catch (error) {
      errorMessage = getErrorMessage(error)
      showErrorMessage(errorMessage)
    } finally {
      isLoadingWalletDetail = false
    }
  }

  async function openWalletAction(component: Component) {
    if (isPreparingWalletAction) {
      return
    }

    isPreparingWalletAction = true

    try {
      const hasLocationPermission = await prepareWalletLocationPermission()

      if (!hasLocationPermission) {
        await showLocationPermissionNotice()
      }

      PubSub.publish('DRIVER.CHANGE_PAGE', { component })
    } finally {
      isPreparingWalletAction = false
    }
  }

  async function prepareWalletLocationPermission(): Promise<boolean> {
    const permissionState = await getDriverLocationPermissionState()

    if (permissionState === 'granted') {
      return true
    }

    if (hasRequestedWalletLocationPermission()) {
      return permissionState !== 'denied'
    }

    setWalletLocationPermissionRequested()

    return requestDriverLocationPermission()
  }

  function hasRequestedWalletLocationPermission(): boolean {
    try {
      return sessionStorage.getItem(WALLET_LOCATION_PERMISSION_REQUESTED_KEY) === '1'
    } catch {
      return false
    }
  }

  function setWalletLocationPermissionRequested() {
    try {
      sessionStorage.setItem(WALLET_LOCATION_PERMISSION_REQUESTED_KEY, '1')
    } catch {
      // Ignore storage failures. Browser permission state remains the source of truth.
    }
  }

  function showLocationPermissionNotice() {
    return Swal.fire({
      icon: 'warning',
      title: i18n($lang, ['Location Permission', 'ການອະນຸຍາດຕຳແໜ່ງ']),
      text: i18n($lang, [
        'Location is not enabled. You can continue, but Loca may need location access for wallet security checks.',
        'ຍັງບໍ່ໄດ້ເປີດຕຳແໜ່ງ. ທ່ານສາມາດດຳເນີນຕໍ່ໄດ້ ແຕ່ Loca ອາດຕ້ອງໃຊ້ຕຳແໜ່ງເພື່ອກວດສອບຄວາມປອດໄພຂອງກະເປົາເງິນ.',
      ]),
      confirmButtonText: i18n($lang, ['Continue', 'ດຳເນີນຕໍ່']),
      confirmButtonColor: '#1CAAFC',
    })
  }
</script>

<div class="page flex min-h-full flex-col bg-[#EFEFEF]">
  <Navbar showBack={true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageHome })}/>

  <img src="{new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}" width="100" class="-mt-10 mx-auto" alt="Loca">

  <div class="m-4 mt-6 text-center text-xl font-semibold">
    {i18n($lang, ['Wallet', 'ກະເປົາເງິນຄົນຂັບ'])}
  </div>

  <div class="mx-4">
    {#if isLoadingWalletDetail}
      <section class="rounded-lg bg-white p-4 shadow">
        <div class="mx-auto h-4 w-40 animate-pulse rounded bg-gray-200"></div>
        <div class="mx-auto mt-5 h-10 w-48 animate-pulse rounded bg-gray-200"></div>
        <div class="mx-auto mt-5 h-px max-w-[280px] bg-gray-200"></div>
        <div class="mt-4 grid grid-cols-2 gap-2">
          <div class="h-14 animate-pulse rounded bg-gray-100"></div>
          <div class="h-14 animate-pulse rounded bg-gray-100"></div>
        </div>
        <div class="mt-5 h-12 animate-pulse rounded bg-gray-100"></div>
      </section>
    {:else if walletSummary}
      <WalletSummaryCard
        summary={walletSummary}
        isActionPending={isPreparingWalletAction}
        onPayNow={() => openWalletAction(PageWalletPayNow)}
        onWithdraw={() => openWalletAction(PageWalletWithdraw)}
      />
    {:else}
      <section class="rounded-lg bg-white p-4 text-center shadow">
        <div class="text-base font-bold text-blue-dark">
          {i18n($lang, ['Cannot load wallet detail', 'ບໍ່ສາມາດໂຫຼດຂໍ້ມູນກະເປົາເງິນໄດ້'])}
        </div>
        <div class="mt-2 text-sm text-gray-500">{errorMessage}</div>
        <button
          type="button"
          class="mt-4 rounded-lg bg-blue-primary px-5 py-2.5 text-sm font-bold uppercase text-white shadow-sm"
          on:click={fetchWalletDetail}
        >
          {i18n($lang, ['Try Again', 'ລອງໃໝ່'])}
        </button>
      </section>
    {/if}
  </div>

  <section class="m-4 rounded-lg bg-white p-4 shadow">
    <h2 class="text-base font-bold text-blue-dark">
      {i18n($lang, ['How this balance works', 'ວິທີເບິ່ງຍອດເງິນນີ້'])}
    </h2>

    <div class="mt-4 space-y-3 text-sm leading-6 text-gray-500">
      <p>
        {i18n($lang, [
          'If the balance is negative, it is the amount the driver needs to pay to Loca.',
          'ຖ້າຍອດເງິນເປັນຄ່າລົບ ແມ່ນຈຳນວນທີ່ຄົນຂັບຕ້ອງຊຳລະໃຫ້ Loca.',
        ])}
      </p>
      <p>
        {i18n($lang, [
          'If the balance is positive, it is the amount available to withdraw to the driver bank account.',
          'ຖ້າຍອດເງິນເປັນຄ່າບວກ ແມ່ນຈຳນວນທີ່ສາມາດຖອນເຂົ້າບັນຊີທະນາຄານໄດ້.',
        ])}
      </p>
    </div>

    <div class="mt-5 space-y-3 border-t border-gray-100 pt-4 text-sm text-gray-500">
      <div class="flex gap-3">
        <span class="mt-1 h-4 w-4 shrink-0 rounded-sm bg-amber-500"></span>
        <div>{i18n($lang, ['Soft Limit: About to reach cash limit.', 'ວົງເງິນຕິດລົບຂັ້ນຕໍ່າ: ໃຫ້ບໍລິການໄດ້ ແຕ່ຄວນເຕີມມາ.'])}</div>
      </div>
      <div class="flex gap-3">
        <span class="mt-1 h-4 w-4 shrink-0 rounded-sm bg-red-500"></span>
        <div>{i18n($lang, ['Hard Limit: Cash bookings may be blocked until payment is completed.', 'ວົງເງິນຕິດລົບສູງສຸດ: ລະບົບໂລກ້າຈະບໍ່ສົ່ງລູກຄ້າໃຫ້, ຕ້ອງເຕິມເງິນເຂົ້າມາ.'])}</div>
      </div>
    </div>
  </section>
</div>
